import { Controller } from '@hotwired/stimulus';
import csrfToken from '@cta/shared/csrf';

import '@campact/camper/icons/CmprIconBrandsSquareFacebook';
import '@campact/camper/icons/CmprIconBrandsSquareInstagram';
import '@campact/camper/icons/CmprIconBrandsSquareXTwitter';
import '@campact/camper/icons/CmprIconBrandsSquareWhatsapp';
import '@campact/camper/icons/CmprIconBrandsTelegram';
import '@campact/camper/icons/CmprIconSolidCommentSms';
import '@campact/camper/icons/CmprIconSolidSquareEnvelope';
import '@campact/camper/icons/CmprIconSolidSquarePhone';

export default class extends Controller {
  static targets = ['counter', 'list'];

  static values = { url: String, relativeDatetimeFallback: String, usernameFallback: String };

  backoffSeconds = [5, 5, 5, 15, 15, 15, 30, 30, 30, 60, 60, 60, 300];

  timeout;

  alreadySubmitted = {};

  submit(event) {
    if (this.alreadySavedIntention(event.target.attributes.interaction.value)) {
      return;
    }

    fetch(event.params.url, {
      method: 'post',
      headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrfToken() },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.type === 'redirect') {
          window.location.assign(response.url);
        } else {
          this.getContactIntentions();
        }
      });
  }

  connect() {
    clearTimeout(this.timeout);

    this.fillContactIntentionsRecursive();
  }

  fillContactIntentionsRecursive() {
    this.getContactIntentions();
    this.timeout = setTimeout(() => this.fillContactIntentionsRecursive(), this.getSeconds() * 1000);
  }

  async getContactIntentions() {
    const response = await fetch(this.urlValue);
    const json = await response.json();

    this.listTarget.innerHTML = json.contact_intentions.map(
      (item) => this.constructor.itemTemplate(item, this.relativeDatetimeFallbackValue, this.usernameFallbackValue),
    ).join('');

    this.counterTarget.innerHTML = json.meta.total_contact_intentions_count;
  }

  getSeconds() {
    return this.backoffSeconds.length > 1 ? this.backoffSeconds.shift() : this.backoffSeconds[0];
  }

  alreadySavedIntention(interaction) {
    const alreadySubmitted = this.alreadySubmitted[interaction] === undefined
      || (new Date() - this.alreadySubmitted[interaction]) > 5000;

    this.alreadySubmitted[interaction] = new Date();

    return !alreadySubmitted;
  }

  static formatRelativeDateTime(datetimeString, fallback) {
    const locale = document.querySelector('html').getAttribute('lang');
    const timeFormat = new Intl.RelativeTimeFormat(locale, { caseFirst: 'upper' });
    const agoInSeconds = (new Date(datetimeString) - Date.now()) / 1000;

    if (agoInSeconds <= -86400) return timeFormat.format(Math.round(agoInSeconds / 86400), 'days');
    if (agoInSeconds <= -3600) return timeFormat.format(Math.round(agoInSeconds / 3600), 'hours');
    if (agoInSeconds <= -60) return timeFormat.format(Math.round(agoInSeconds / 60), 'minutes');
    if (agoInSeconds <= -10) return timeFormat.format(Math.round(agoInSeconds), 'seconds');
    return fallback;
  }

  static capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  static iconTag(icon) {
    const tag = {
      messenger: 'cmpr-icon-brands-square-facebook',
      facebook: 'cmpr-icon-brands-square-facebook',
      instagram: 'cmpr-icon-brands-square-instagram',
      twitter: 'cmpr-icon-brands-square-x-twitter',
      whatsapp: 'cmpr-icon-brands-square-whatsapp',
      telegram: 'cmpr-icon-brands-telegram',
      sms: 'cmpr-icon-solid-comment-sms',
      envelope: 'cmpr-icon-solid-square-envelope',
      phone: 'cmpr-icon-solid-square-phone',
    }[icon];

    return `<${tag}
      class="contact-call_contact-intentions__item__icon contact-call_contact-intentions__item__icon--${icon}"
      ></${tag}>`;
  }

  static usernameTag(someone, fallback) {
    return someone ? `<strong>${someone}</strong>` : fallback;
  }

  static dateTimeTag(createdAt, fallback) {
    return `<time
    class="contact-call_contact-intentions__item__date pl-40"
    >${this.capitalizeFirstLetter(this.formatRelativeDateTime(createdAt, fallback))}</time>`;
  }

  static itemTemplate(json, dateTimeFallback, usernameFallback) {
    return `<li class="contact-call_contact-intentions__item py-40">
    ${this.iconTag(json.icon)}
    <div class="contact-call_contact-intentions__item__text">
    ${this.usernameTag(json.username, usernameFallback)}
    ${json.text}${this.dateTimeTag(json.created_at, dateTimeFallback)}</div>
    </li>`;
  }
}
